import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHelmet from '@/components/base/SEOHelmet';

const siteUrl = 'https://nlafv.cn';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '樱花视频',
  url: siteUrl,
  description: '唯美樱花视频素材与观赏指南，高清樱花视频免费下载',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '樱花视频',
  url: siteUrl,
  description: '唯美樱花视频素材与观赏指南平台',
  sameAs: [
    'https://weibo.com/sakuravideo',
    'https://space.bilibili.com/sakuravideo',
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '樱花视频的素材可以免费下载吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，樱花视频平台上的大部分视频素材都支持免费下载使用。我们提供4K和1080P两种分辨率供选择，适用于个人创作和商业项目。部分素材可能需要署名，请在使用前查看具体授权说明。',
      },
    },
    {
      '@type': 'Question',
      name: '日本樱花最佳观赏时间是什么时候？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '日本樱花花期因地理位置不同而有差异：冲绳最早（1月下旬），九州（3月下旬），关西、关东地区（3月底至4月初），东北地区（4月中下旬），北海道最晚（4月底至5月中旬）。建议关注日本气象协会发布的樱花开花预想。',
      },
    },
    {
      '@type': 'Question',
      name: '如何拍摄唯美的樱花视频？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '拍摄樱花视频建议选择清晨或傍晚的黄金时刻，利用逆光突出花瓣的透明质感，使用大光圈虚化背景。拍摄樱花飘落建议使用高帧率（60fps以上），后期可放慢获得流畅的慢动作效果。延时摄影需要准备三脚架和间隔拍摄功能。',
      },
    },
    {
      '@type': 'Question',
      name: '武汉大学樱花什么时候开？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '武汉大学樱花一般在每年3月中旬至3月下旬盛开，具体花期受当年气温影响。樱花季期间武汉大学实行预约入校制度，需提前通过官方渠道预约。建议选择工作日前往，避开周末人流高峰。',
      },
    },
  ],
};

const featuredVideos = videos.filter((v) => v.featured);

export default function Home() {
  return (
    <>
      <SEOHelmet
        title="唯美樱花视频素材与观赏指南"
        description="樱花视频精选合集，涵盖日本、武汉、北京等地樱花盛开实拍视频，提供高清樱花视频素材免费下载，记录每一帧飘落之美。"
        keywords="樱花视频,高清樱花视频素材,樱花视频免费下载,日本樱花实拍视频,樱花飘落视频,唯美樱花延时摄影,樱花季观赏视频"
        canonicalUrl={siteUrl}
        ogType="website"
        schema={{ ...homeSchema, ...orgSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Stunning%20cherry%20blossom%20landscape%20with%20ancient%20Japanese%20temple%20in%20distance%2C%20pink%20sakura%20trees%20in%20full%20bloom%2C%20soft%20morning%20light%2C%20petals%20falling%20gently%2C%20dreamy%20atmosphere%2C%20cinematic%20wide%20angle%2C%20warm%20color%20palette%2C%20serene%20natural%20scenery&width=1920&height=1080&seq=hero-home-01&orientation=landscape"
            alt="唯美樱花盛开全景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
              记录每一帧<span className="text-primary-300">飘落之美</span>
            </h1>
            <p className="text-sm md:text-lg text-white/80 max-w-2xl mb-6 md:mb-8 leading-relaxed">
              樱花视频精选合集，涵盖日本、武汉、北京等地樱花盛开实拍视频，提供高清樱花视频素材免费下载
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/hd-footage"
                className="px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览高清素材
              </Link>
              <Link
                to="/category/japan-sakura"
                className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                日本樱花实拍
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              精选视频分类
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              按地点、类型和风格浏览，找到你想要的樱花视频素材
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group relative rounded-lg overflow-hidden h-56 md:h-64 cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20">
                      <i className={`${cat.icon} text-white text-sm`}></i>
                    </span>
                    <h3 className="text-lg font-heading font-semibold text-white">{cat.name}</h3>
                  </div>
                  <p className="text-xs text-white/70 line-clamp-2 mb-2">{cat.description}</p>
                  <span className="text-xs text-white/50">{cat.count} 个视频</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Videos Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8 md:mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  精选推荐视频
                </h2>
                <p className="text-sm text-foreground-600">编辑精选的高质量樱花视频</p>
              </div>
              <Link
                to="/category/hd-footage"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {featuredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/category/${categories.find((c) => c.id === video.categoryId)?.slug || ''}`}
                  className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-full">
                      {video.resolution}
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {video.views.toLocaleString()}
                      </span>
                      <span>{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/category/hd-footage"
                className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部视频 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                最新文章
              </h2>
              <p className="text-sm text-foreground-600">赏樱攻略、摄影技巧与樱花文化</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {articles.slice(0, 6).map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                      {article.categoryName}
                    </span>
                    <span className="text-xs text-foreground-500">{article.readTime}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于樱花视频的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqSchema.mainEntity.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/60">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <h4 className="text-sm font-semibold text-foreground-900 pr-4">
                      <a href={`#faq-${idx}`}>{faq.name}</a>
                    </h4>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:rotate-45 transition-transform">
                      <i className="ri-add-line text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20cherry%20blossom%20background%20with%20floating%20pink%20petals%2C%20dreamy%20ethereal%20atmosphere%2C%20gentle%20gradient%20from%20white%20to%20soft%20pink%2C%20minimal%20artistic%20style%2C%20clean%20elegant%20composition&width=1920&height=600&seq=cta-home-02&orientation=landscape"
            alt="樱花背景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30"></div>
          <div className="relative max-w-2xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              寻找更多樱花视频素材？
            </h2>
            <p className="text-sm text-white/70 mb-6">
              浏览我们的高清素材库，发现更多唯美樱花视频
            </p>
            <Link
              to="/category/hd-footage"
              className="inline-flex px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即浏览素材库
            </Link>
          </div>
        </section>

        {/* Structured Data Script */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </main>

      <Footer />
    </>
  );
}